import React from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { NotForExternalRoute } from '../../components/NotForExternalRoute';
import { lazyWithRetry } from '../../lib/lazyWithRetry';
import { VENDORS_ADMIN_ENABLED } from '../../config/vendors';
import { guardPage } from '../pageGuard';
import { BILL_VIEW_PAGES, FINANCE_PAGE } from '../pageAccess';

const ProjectsDashboard = lazyWithRetry(() => import('../../pages/Projects/ProjectsDashboard'));
const ProjectBoard = lazyWithRetry(() => import('../../pages/Projects/ProjectBoard'));
const ProjectDetail = lazyWithRetry(() => import('../../pages/Projects/ProjectDetail'));
const ProjectEquipmentList = lazyWithRetry(() => import('../../pages/Projects/ProjectEquipmentList'));
const ProjectEquipmentDetail = lazyWithRetry(() => import('../../pages/Projects/ProjectEquipmentDetail'));
const ProjectsCalendarPage = lazyWithRetry(() => import('../../pages/Projects/ProjectsCalendarPage'));
const MeetingMode = lazyWithRetry(() => import('../../pages/Projects/MeetingMode'));
const ChecklistTemplateEditor = lazyWithRetry(() => import('../../pages/Projects/ChecklistTemplateEditor'));
const AgencyDirectory = lazyWithRetry(() => import('../../pages/Projects/AgencyDirectory'));
const ProjectSettings = lazyWithRetry(() => import('../../pages/Projects/ProjectSettings'));
const InvoiceMatching = lazyWithRetry(() => import('../../pages/Projects/InvoiceMatching'));
const ProjectBillingStatus = lazyWithRetry(() => import('../../pages/Projects/ProjectBillingStatus'));
const BackfillReviewQueue = lazyWithRetry(() => import('../../pages/Projects/BackfillReviewQueue'));
const EquipmentBillMatching = lazyWithRetry(() => import('../../pages/Projects/EquipmentBillMatching'));

export const routes: RouteObject[] = [
  {
    index: true,
    element: <ProtectedRoute><ProjectsDashboard /></ProtectedRoute>,
  },
  {
    path: 'board',
    element: <ProtectedRoute><ProjectBoard /></ProtectedRoute>,
  },
  {
    path: 'invoice-matching',
    // Linking invoices to projects is money work: Billing (P4) only (Nick 2026-10-02). Projects
    // users get the read-only /projects/billing-status instead.
    ...guardPage(FINANCE_PAGE, <InvoiceMatching />),
  },
  {
    // Read-only bill view (Nick 2026-10-02). Must stay ABOVE ':id'. Creating bills is P4 only.
    path: 'billing-status',
    ...guardPage(BILL_VIEW_PAGES.projects, <ProjectBillingStatus />),
  },
  {
    path: 'backfill',
    element: <ProtectedRoute><NotForExternalRoute><BackfillReviewQueue /></NotForExternalRoute></ProtectedRoute>,
  },
  // Scheduling vendors are a manager-only TAB inside Project Settings (Nick, 2026-10-15); the old address forwards there so
  // links and bookmarks keep working (the tab applies the manager/external gates). Must stay ABOVE ':id', or "vendors" is
  // read as a project id.
  ...(VENDORS_ADMIN_ENABLED
    ? [{ path: 'vendors', element: <Navigate to="/projects/settings?tab=vendors" replace /> }]
    : []),
  {
    path: ':id',
    element: <ProtectedRoute><ProjectDetail /></ProtectedRoute>,
  },
  {
    path: 'equipment/:equipmentId',
    element: <ProtectedRoute><ProjectEquipmentDetail /></ProtectedRoute>,
  },
  {
    path: 'equipment/bill-matching',
    ...guardPage(FINANCE_PAGE, <EquipmentBillMatching />),
  },
  {
    path: 'equipment',
    element: <ProtectedRoute><ProjectEquipmentList /></ProtectedRoute>,
  },
  {
    // ADR-004 retired the Projects module's TWO calendars (a react-big-calendar
    // month view and a separate Install-only technician matrix) because three
    // surfaces over one schedule-block collection meant a block created on one
    // was invisible on another.
    //
    // Round 7 restores a Projects ENTRY POINT — not a third calendar. This
    // renders the identical SchedulerMatrix that /field-service/schedule
    // renders, over the same blocks and lanes, just opening on the Install
    // tab. One calendar, two doors.
    path: 'calendar',
    element: <ProtectedRoute><ProjectsCalendarPage /></ProtectedRoute>,
  },
  {
    // The retired second calendar. Still a redirect — there is exactly one
    // Projects calendar route now.
    path: 'scheduler',
    element: <Navigate to="/projects/calendar" replace />,
  },
  {
    path: 'meeting',
    element: <ProtectedRoute><NotForExternalRoute><MeetingMode /></NotForExternalRoute></ProtectedRoute>,
  },
  {
    path: 'templates',
    element: <ProtectedRoute><ChecklistTemplateEditor /></ProtectedRoute>,
  },
  {
    path: 'agencies',
    element: <ProtectedRoute><AgencyDirectory /></ProtectedRoute>,
  },
  {
    path: 'settings',
    element: <ProtectedRoute><ProjectSettings /></ProtectedRoute>,
  },
];
